import {
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InfoComponent } from "@components/info/info.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import moment from "moment";
import { Subject, firstValueFrom } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { MaskinlagEntreprenorInfo } from "~app/common/models/arbetsorder/maskinlag/GetMaskinlagEntreprenorerResponseDTO";
import { MaskinlagtabInformationDTO } from "~app/common/models/arbetsorder/maskinlag/MaskinlagtabInformationDTO";
import {
    Arbetsordertyper,
    ConfirmDialogResponse,
    Maskinlagstyp,
    Operationstyper,
    Produktionsstatus,
    VirkesorderStatus
} from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CalendarService } from "~app/common/services/calendar.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FormatService } from "~app/common/services/format.service";
import { MaskinlagService } from "~app/common/services/maskinlag.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SetAnsvarigAvverkningledareCommand } from "~app/features/planera/commands/SetAnsvarigAvverkningledareCommand";
import { IMaskinLagWithOperations, IMaskinlagForArbetsorderResponse } from "~app/features/planera/mina-maskinlag/mina-maskinlag.models";
import { ArbetsorderDisabledFlag } from "~app/features/planera/model/ArbetsorderDisabledFlag";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IMaskinlag } from "~app/features/planera/model/IMaskinLag";
import { IOperationBase } from "~app/features/planera/model/IOperationBase";
import { environment } from "~environments/environment";
import { ResizeArbetsorderOperationCommand } from "../commands/ResizeArbetsorderOperationCommand";
import { SetFordrojningSkotningCommand } from "../commands/SetFordrojningSkotningCommand";
import { SetJusteradPrestationCommand } from "../commands/SetJusteradPrestationCommand";
import { SetMaskinlagCommand } from "../commands/SetMaskinlagCommand";
import { ArbetsorderMaskinlagUpdatedMessage } from "../events/ArbetsorderMaskinlagUpdatedMessage";
import { ArbetsorderOperationDeletedEvent } from "../events/ArbetsorderOperationDeletedEvent";
import { ArbetsorderOperationUpdatedEvent } from "../events/ArbetsorderOperationUpdatedEvent";
import { MinaMaskinlagUpdatedEventMessage } from "../events/MinaMaskinlagUpdatedEventMessage";
import { MinaMaskinlagDialogService } from "../mina-maskinlag/mina-maskinlag-dialog.service";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { ArbetsorderService } from "../services/arbetsorder.service";
import { ArbetsorderMaskinlagService } from "./arbetsorder-maskinlag.service";
import { ArbetsorderMaskinlagTexts } from "./arbetsorder-maskinlag.texts";

@Component({
    selector: "rask-arbetsorder-maskinlag",
    templateUrl: "./arbetsorder-maskinlag.component.html",
    styleUrls: ["./arbetsorder-maskinlag.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        InputComponent,
        ButtonComponent,
        DatepickerComponent,
        InputNumberComponent,
        InfoComponent
    ]
})
export class ArbetsorderMaskinlagComponent implements OnInit, OnChanges, OnDestroy {
    arbetsorder: IArbetsorderDetail;
    egetMaskinlag: boolean;
    entreprenorSaknas = false;
    entreprenorer: MaskinlagEntreprenorInfo[] = [];
    linkToMaskinregister = environment.maskinregisterUrl;
    maskinlagInfo: MaskinlagtabInformationDTO;
    maskinlagLoaded = false;
    selectedMaskinlag: IMaskinLagWithOperations;
    selectedMinaMaskinlag: IMaskinLagWithOperations[];
    showMaskinoperationInfo = false;
    texts = ArbetsorderMaskinlagTexts.texts;

    private readonly arbetsorderMaskinlagService = inject(ArbetsorderMaskinlagService);
    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly calendarService = inject(CalendarService);
    private readonly eventService = inject(EventService);
    private readonly formatService = inject(FormatService);
    private readonly maskinlagService = inject(MaskinlagService);
    private readonly minaMaskinlagDialogService = inject(MinaMaskinlagDialogService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);

    private maskinlagChanged = false;
    private maskinlagRollbackInProgress = false;
    private selectedEntreprenorField: MaskinlagEntreprenorInfo;

    get angivenFordrojningSkotning(): number {
        return this.maskinlagInfo.angivenFordrojningSkotning;
    }

    set angivenFordrojningSkotning(value: number) {
        if (value === this.maskinlagInfo.fordrojningSkotning) {
            // Ignore change with unmodified value
            return;
        }

        if (ObjectUtils.isNullOrUndefined(value) && ObjectUtils.isNullOrUndefined(this.maskinlagInfo.fordrojningSkotning)) {
            return;
        }

        if (!ObjectUtils.isNullOrUndefined(value) && value < 0) {
            value = null;
        }

        this.maskinlagInfo.angivenFordrojningSkotning = value; // Triggers another change which is ignored at the top
        this.arbetsorder.commandManager.addOrUpdate(new SetFordrojningSkotningCommand(c => {
            c.arbetsorderId = this.arbetsorder.id;
            c.angivenFordrojningSkotning = this.angivenFordrojningSkotning;
        }), c => c.arbetsorderId === this.arbetsorder.id);
    }

    get ansvarigAvverkningledare(): string {
        if (this.arbetsorder && this.arbetsorder.ansvarigAvverkningsledare && this.arbetsorder.ansvarigAvverkningsledare.fornamn) {
            return `${this.arbetsorder.ansvarigAvverkningsledare.fornamn} ${this.arbetsorder.ansvarigAvverkningsledare.efternamn}`;
        } else {
            return "-";
        }
    }

    get antalSkordarArbetsdagar(): number {
        return this.antalArbetsdagar(this.skordningStartdatum, this.skordningSlutdatum);
    }

    get antalSkordardagar(): string {
        return this.antalDagar(this.skordningStartdatum, this.skordningSlutdatum);
    }

    get antalSkotningArbetsdagar(): number {
        return this.antalArbetsdagar(this.skotningStartdatum, this.skotningSlutdatum);
    }

    get antalSkotningdagar(): string {
        return this.antalDagar(this.skotningStartdatum, this.skotningSlutdatum);
    }

    get avrundat(): string {
        return ObjectUtils.isNullOrUndefined(this.arbetsorder.volymArbetsorder, this.antalSkordarArbetsdagar) ?
            "-" :
            `${Math.round(this.arbetsorder.volymArbetsorder / this.antalSkordarArbetsdagar)}`;
    }

    get changeFordrojningSkotningDisabled(): boolean {
        return !this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer);
    }

    get changeJusteradPrestationDisabled(): boolean {
        return !this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer);
    }

    get changeMaskinlagDisabled(): boolean {
        return this.arbetsorder && !this.arbetsorder.raskVo && this.arbetsorder.virkesorderStatus.some(p => p.status !== VirkesorderStatus.Makulerad);
    }

    get fordrojningSkotning(): number {
        return this.maskinlagInfo.fordrojningSkotning;
    }

    get isAvverkning(): boolean {
        return this.arbetsorder.typ === Arbetsordertyper.Avverkning;
    }

    get isProduktionGodkand() {
        return this.arbetsorder?.produktionsstatus === Produktionsstatus.ProduktionGodkand;
    }

    get justeradPrestation(): number {
        return this.maskinlagInfo?.justeradPrestation;
    }

    set justeradPrestation(value: number) {
        if (value === this.maskinlagInfo?.justeradPrestation) {
            // Ignore change with unmodified value
            return;
        }

        if (ObjectUtils.isNullOrUndefined(value) && ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.justeradPrestation)) {
            return;
        }

        if (!ObjectUtils.isNullOrUndefined(value) && value <= 0) {
            value = null;
        }

        this.maskinlagInfo.justeradPrestation = value; // Triggers another change which is ignored at the top
        this.arbetsorder.commandManager.addOrUpdate(new SetJusteradPrestationCommand(c => {
            c.arbetsorderId = this.arbetsorder.id;
            c.justeradPrestation = this.justeradPrestation;
        }), c => c.arbetsorderId === this.arbetsorder.id);
    }

    get korrektion(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.justeradPrestation, this.maskinlagInfo?.prestationM3fubTimme, this.maskinlagInfo?.maskinlagTimmarPerDag) ?
            "100%" :
            `${Math.round(100 * (this.maskinlagInfo.justeradPrestation / (this.maskinlagInfo.prestationM3fubTimme * this.maskinlagInfo.maskinlagTimmarPerDag)))}%`; // `
    }

    get maskinlagTimmarPerDag(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.maskinlagTimmarPerDag) ?
            "-" :
            `${Math.round(this.maskinlagInfo.maskinlagTimmarPerDag)}`; // `
    }

    get planeradVolym(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.planeradVolym) ?
            "-" :
            `${Math.round(this.maskinlagInfo.planeradVolym)}`; // `
    }

    get prestationM3fubTimme(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.maskinlagTimmarPerDag, this.maskinlagInfo?.prestationM3fubTimme) ?
            "-" :
            `${Math.round(this.maskinlagInfo.maskinlagTimmarPerDag * this.maskinlagInfo.prestationM3fubTimme)}`; // `
    }

    get selectedEntreprenor(): MaskinlagEntreprenorInfo {
        return ObjectUtils.isNullOrUndefined(this.selectedEntreprenorField) ? null : this.selectedEntreprenorField;
    }

    set selectedEntreprenor(value: MaskinlagEntreprenorInfo) {
        if (ObjectUtils.isNullOrUndefined(value) || value.id === this.selectedEntreprenor?.id) {
            return;
        }
        this.selectedEntreprenorField = value;
    }

    get selectedEntreprenorNamn(): string {
        return ObjectUtils.isNullOrUndefined(this.selectedEntreprenorField) ? null : `${this.selectedEntreprenorField.namn}, ${this.selectedEntreprenorField.orgNummer}` ;
    }

    get skordningSlutdatum(): string {
        const maskinoperation = this.getSkordningsOperation();
        if (maskinoperation) {
            return moment(maskinoperation.slutdatum).subtract(1, "d").format();
        }

        return null;
    }

    set skordningSlutdatum(date: string) {

        if (!date) {
            return;
        }
        const maskinoperation: IOperationBase = this.getSkordningsOperation();

        maskinoperation.slutdatum = this.formatService.dateFormatRask(moment(date).add(1, "d").toISOString(), false);

        this.setUpdateArbetsorderCommand(maskinoperation.startdatum, maskinoperation.slutdatum);
    }

    get skordningStartdatum(): string {
        const maskinoperation = this.getSkordningsOperation();

        if (maskinoperation) {
            return moment(maskinoperation.startdatum).format();
        }

        return null;
    }

    set skordningStartdatum(date: string) {
        if (!date) {
            return;
        }

        let maskinoperation: IOperationBase = this.getSkordningsOperation();

        if (!maskinoperation) {
            const operationstyp = this.isGrotskotningsOperation() ? Operationstyper.Grotskotning : Operationstyper.Skordning;

            maskinoperation = this.arbetsorderService.createOperation(this.arbetsorder, operationstyp, date, null);
        }

        maskinoperation.slutdatum = this.getEndDate(date).startOf("d").format();
        maskinoperation.startdatum = date;

        this.setUpdateArbetsorderCommand(maskinoperation.startdatum, maskinoperation.slutdatum);
    }

    get skotningSlutdatum(): string {
        const maskinoperation = this.getSkotningsOperation();
        return maskinoperation ? this.formatService.dateFormatRask(moment(maskinoperation.slutdatum).subtract(1, "d").toISOString(), false) : "-";
    }

    set skotningSlutdatum(date: string) {
        if (!date) {
            return;
        }

        const maskinoperation = this.getSkotningsOperation();
        maskinoperation.slutdatum = this.formatService.dateFormatRask(moment(date).add(1, "d").toISOString(), false);
    }

    get skotningStartdatum(): string {
        const maskinoperation = this.getSkotningsOperation();
        return maskinoperation ? this.formatService.dateFormatRask(maskinoperation.startdatum, false) : "-";
    }

    set skotningStartdatum(date: string) {
        if (!date) {
            return;
        }

        let maskinoperation = this.getSkotningsOperation();

        if (!maskinoperation) {
            const operationstyp = this.isGrotskotningsOperation() ? Operationstyper.Grotskotning : Operationstyper.Skotning;
            maskinoperation = this.arbetsorderService.createOperation(this.arbetsorder, operationstyp, date, null);
        }
        maskinoperation.startdatum = date;
    }

    get skotningsavstand(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.skotningsavstand) ?
            "-" :
            `${this.maskinlagInfo.skotningsavstand} m`; // `
    }

    get utfall(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.utfall) ?
            "-" :
            `${Math.round(this.maskinlagInfo.utfall)}`; // `
    }

    get utfallPerArbetsdag(): string {
        let dagar: number;

        // Om skördning påbörjats men det planerade startdatumet är i framtiden visas den totala volym som skördats hittills.
        if (moment() < moment(this.skordningStartdatum)) {
            dagar = 1;
        } else {
            const slutdatum = moment.min(moment(this.skordningSlutdatum), moment());
            const antalArbetsdagarSedanSkordningStart = DateUtils.countDaysOfType(
                moment(this.skordningStartdatum),
                slutdatum,
                d => !this.calendarService.isWeekendOrHelgdag(d),
                false);

            dagar = Math.max(antalArbetsdagarSedanSkordningStart, 1);
        }

        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.utfall) ?
            "-" :
            `${Math.round(this.maskinlagInfo.utfall / dagar)}`; // `
    }

    get utfallPerPlanerad(): string {
        return ObjectUtils.isNullOrUndefined(this.maskinlagInfo?.utfall, this.maskinlagInfo?.planeradVolym) ?
            "-" :
            `${Math.round(this.maskinlagInfo.utfall / this.maskinlagInfo.planeradVolym * 100)} %`; // `
    }

    canDelete() {
        return !this.changeMaskinlagDisabled;
    }

    canEdit = () => this.authenticationService.canEdit() && this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad);

    async findNextAvailableSpaceInSchedule(): Promise<void> {
        const scheduleResult = await this.arbetsorderMaskinlagService.getNextAvailableSpaceInSchedule(this.arbetsorder.id, this.selectedMaskinlag.id);
        if (this.arbetsorder.isGrotskotning) {
            this.skotningStartdatum = scheduleResult.startDate;
            this.skotningSlutdatum = moment(scheduleResult.endDate).subtract(1, "d").format();

            this.setUpdateArbetsorderCommand(scheduleResult.startDate, scheduleResult.endDate);
        } else {
            this.skordningStartdatum = scheduleResult.startDate;
            this.skordningSlutdatum = moment(scheduleResult.endDate).subtract(1, "d").format();
        }
    }

    getEndDate(startDate) {
        const oldWorkdays = Math.round(this.maskinlagInfo.planeradVolym /
            (this.maskinlagInfo.maskinlagTimmarPerDag * this.maskinlagInfo.prestationM3fubTimme));

        let skordningSlut = moment(startDate).add(oldWorkdays, "d"); // Minst såhär långt fram
        const dateStart = moment(startDate);
        let currentWorkdays = this.workdaysUntil(dateStart, skordningSlut);
        while (currentWorkdays !== oldWorkdays) {
            skordningSlut = moment(skordningSlut).add(1, "d");
            currentWorkdays = this.workdaysUntil(dateStart, skordningSlut);
        }

        return skordningSlut;
    }

    handleMinaMaskinlagUpdated = async (event: MinaMaskinlagUpdatedEventMessage) => {
        await this.loadMaskinlag();
    };

    maskinlagSelectionChanged(newObj: IMaskinLagWithOperations): void {
        if (this.maskinlagRollbackInProgress || this.arbetsorder.maskinlagId === newObj?.id
            || (ObjectUtils.isNullOrUndefined(this.arbetsorder.maskinlagId) && ObjectUtils.isNullOrUndefined(newObj?.id))) {
            return;
        }

        if (this.arbetsorder.produktionsstatus !== Produktionsstatus.ProduktionEjPaborjad) {
            setTimeout(() => { // Wrappar i timeout för att förhindra 'Expression has changed after it was checked'

                const text = "Tänk på att om du byter maskinlag efter det att produktion är påbörjad " +
                    "kan det eventuellt krävas manuell hantering vid fakturering. " +
                    "Vill du fortsätta?";
                this.raskModalService.confirm(new ModalSettings("Byt maskinlag", text))

                    .subscribe({
                        next: (response) => {
                            if (response === ConfirmDialogResponse.save) {
                                this.setSelectedMaskinlag(newObj);
                            } else {
                                this.maskinlagRollbackInProgress = true;
                                const tmp = this.selectedMaskinlag;
                                this.selectedMaskinlag = undefined;
                                setTimeout(() => {
                                    this.selectedMaskinlag = tmp;
                                    this.maskinlagRollbackInProgress = false;
                                }, 0);
                            }
                        },
                        error: () => {
                            // Sätter tillbaks selectedMaskinlag till föregående via null för att selector.component ska uppdatera sitt interna värde
                            this.maskinlagRollbackInProgress = true;
                            const tmp = this.selectedMaskinlag;
                            this.selectedMaskinlag = undefined;
                            setTimeout(() => {
                                this.selectedMaskinlag = tmp;
                                this.maskinlagRollbackInProgress = false;
                            }, 0);
                        }
                    });
            }, 0);

        } else {
            this.setSelectedMaskinlag(newObj);
        }
    }

    maskinlagViewFormatter = (result: IMaskinLagWithOperations): string => result ? `${result.csrNummer} ${result.namn}` : null; // `

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.arbetsorder && changes.arbetsorder?.previousValue?.id !== this.arbetsorder.id) {
            await this.setup();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    async ngOnInit() {
        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                if (changeEvent != null) {
                    this.arbetsorder = changeEvent?.arbetsorderWrapper?.arbetsorder;
                }
            });

        const debounce = debounceTime(100);

        this.eventService.subscribeEvent<ArbetsorderOperationUpdatedEvent>(ArbetsorderOperationUpdatedEvent.$type)
            .pipe(debounce)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleOperationUpdatedEvent);

        this.eventService.subscribeEvent<ArbetsorderOperationDeletedEvent>(ArbetsorderOperationDeletedEvent.$type)
            .pipe(debounce)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleOperationUpdatedEvent);

        this.eventService.subscribeEvent<MinaMaskinlagUpdatedEventMessage>(MinaMaskinlagUpdatedEventMessage.$type)
            .pipe(debounce)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleMinaMaskinlagUpdated);

        this.eventService.subscribeEvent<ArbetsorderMaskinlagUpdatedMessage>(ArbetsorderMaskinlagUpdatedMessage.$type)
            .pipe(debounce)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleOperationUpdatedEvent);

        await this.setup();
    }

    openMinaMaskinlag(): void {
        setTimeout(() => {
            firstValueFrom(this.minaMaskinlagDialogService.minaMaskinlagModal())
                .then(r => this.loadMaskinlag())
                .catch(e => this.loadMaskinlag());
        }, 0);
    }

    async reloadMaskinlagInfo(): Promise<void> {
        this.maskinlagInfo = await this.arbetsorderMaskinlagService
            .getMaskinlagtabInfo(this.arbetsorder.id, this.selectedMaskinlag?.id);
        this.showMaskinoperationInfo = true;
    }

    setInloggadAnsvarigAvverkningsledare(): void {
        const currentUser = this.authenticationService.getUserData();
        this.arbetsorder.commandManager.addOrUpdate(new SetAnsvarigAvverkningledareCommand(c => {
            c.arbetsorderId = this.arbetsorder.id;
            c.username = currentUser.userName;
        }), c => c.$type === SetMaskinlagCommand.$type);

        this.arbetsorderService.getAnvandare(currentUser.userName).then(x => {
            this.arbetsorder.ansvarigAvverkningsledare = x;
        });
    }

    workdaysUntil(dateFrom: moment.Moment, dateTo: moment.Moment) {
        const daysBetween = DateUtils.daysBetween(dateTo, dateFrom);
        const holidays = DateUtils.countDaysOfType(dateFrom, dateTo, d => this.calendarService.isWeekendOrHelgdag(d));
        return daysBetween - holidays;
    }

    private antalArbetsdagar(startdatum: string, slutdatum: string): number {
        if (this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer)) {
            return DateUtils.countDaysOfType(
                moment(startdatum),
                moment(slutdatum),
                d => !this.calendarService.isWeekendOrHelgdag(d),
                false);
        } else {
            const antalDagar = this.maskinlagInfo?.planeradVolym / (this.maskinlagInfo?.maskinlagTimmarPerDag * this.maskinlagInfo?.prestationM3fubTimme);
            if (!isNaN(antalDagar)) {
                return Math.round(antalDagar);
            }
            return 0;
        }
    }

    private antalDagar(startdatum: string, slutdatum: string): string {
        if (this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer)) {
            return (moment(slutdatum).diff(moment(startdatum), "d") + 1).toString();
        } else {
            return "-";
        }
    }

    private getSkordningsOperation(): IOperationBase {
        const operationstyp = this.isGrotskotningsOperation() ? Operationstyper.Grotskotning : Operationstyper.Skordning;
        return this.arbetsorderService.getOperationInArbetsorder(this.arbetsorder.operationer, operationstyp);
    }

    private getSkotningsOperation(): IOperationBase {
        const operationstyp = this.isGrotskotningsOperation() ? Operationstyper.Grotskotning : Operationstyper.Skotning;
        return this.arbetsorderService.getOperationInArbetsorder(this.arbetsorder.operationer, operationstyp);
    }

    private async loadMaskinlag(): Promise<void> {
        const filterMaskinlag = (maskinlag: IMaskinlag): boolean => {
            if (!this.arbetsorder) {
                return false;
            }

            const isMatch = this.arbetsorder.maskinlagId === maskinlag.id;

            return isMatch ||
                (this.arbetsorder.isGrotskotning
                    ? true // Grotskotning ska tillåta samliga maskinlag
                    : maskinlag.maskinlagstyp !== Maskinlagstyp.Grot);
        };

        let response: IMaskinlagForArbetsorderResponse;

        if (this.arbetsorder) {
            response = await this.maskinlagService
                .getMaskinlagForArbetsorder(this.arbetsorder.id, null);
        } else {
            response = await this.maskinlagService
                .getMinaMaskinlagAndOperationer(new Date());
        }

        this.selectedMinaMaskinlag = response.maskinlag.filter(filterMaskinlag);
        this.selectedMaskinlag = this.selectedMinaMaskinlag.find(m => m.id === this.arbetsorder.maskinlagId);
    }

    private readonly setupEntreprenor = async (): Promise<void> => {
        if (!ObjectUtils.isNullOrUndefined(this.arbetsorder.maskinlagId)) {
            const response = await this.arbetsorderMaskinlagService.getMaskinlagEntreprenorer(this.arbetsorder.id, this.arbetsorder.maskinlagId);
            this.entreprenorer = response.entreprenorer;
            this.egetMaskinlag = response.egetLag;
            if (!ObjectUtils.isNullOrUndefined(this.arbetsorder.entreprenorId) && this.entreprenorer.length > 0) {
                this.selectedEntreprenorField = this.entreprenorer.find(p => p.id === this.arbetsorder.entreprenorId);
            }

            this.entreprenorSaknas = this.entreprenorer.length === 0 && !ObjectUtils.isNullOrUndefined(this.arbetsorder.maskinlagId) && !this.maskinlagChanged;
        }
    };

    private readonly handleOperationUpdatedEvent = async (event: ArbetsorderOperationUpdatedEvent & ArbetsorderMaskinlagUpdatedMessage): Promise<void> => {
        this.showMaskinoperationInfo = false;
        this.maskinlagChanged = false;

        await this.loadMaskinlag();
        await this.setupEntreprenor();

        if ((event.arbetsorderId === this.arbetsorder.id || event.arbetsorderGuid === this.arbetsorder.guid) && event.maskinlagId) {
            this.maskinlagInfo = await this.arbetsorderMaskinlagService.getMaskinlagtabInfo(this.arbetsorder.id);
            this.showMaskinoperationInfo = true;
        }
    };

    private isGrotskotningsOperation(): boolean {
        return this.arbetsorder.operationer.findIndex(o => o.operationstyp === Operationstyper.Grotskotning) !== -1;
    }

    private setSelectedMaskinlag(value: IMaskinLagWithOperations) {
        if ((!value && this.arbetsorder.maskinlagId) || (value && value.id !== this.arbetsorder.maskinlagId)) {
            this.arbetsorder.maskinlagId = value ? value.id : null;
            this.maskinlagChanged = true;
            this.selectedMaskinlag = this.selectedMinaMaskinlag.find(m => m.id === this.arbetsorder.maskinlagId);
            this.showMaskinoperationInfo = false;

            this.arbetsorder.commandManager.addOrUpdate(new SetMaskinlagCommand(c => {
                c.arbetsorderId = this.arbetsorder.id;
                c.maskinlagId = this.arbetsorder.maskinlagId;
            }), c => c.$type === SetMaskinlagCommand.$type);

            if (this.arbetsorder.ansvarigAvverkningsledare.userId === null
                && this.arbetsorder.ansvarigAvverkningsledare.fornamn === null
                && this.arbetsorder.ansvarigAvverkningsledare.efternamn === null) {
                this.arbetsorder.commandManager.addOrUpdate(new SetAnsvarigAvverkningledareCommand(c => {
                    c.arbetsorderId = this.arbetsorder.id;
                    c.username = value.ansvarigUserName;
                }), c => c.$type === SetAnsvarigAvverkningledareCommand.$type);
                this.arbetsorderService.getAnvandare(value.ansvarigUserName).then(x => {
                    this.arbetsorder.ansvarigAvverkningsledare = x;
                });
            }

            this.selectedEntreprenorField = null;
            this.arbetsorder.entreprenorId = null;
            this.egetMaskinlag = false;
            this.entreprenorer = [];

            this.arbetsorder.operationer.splice(0, this.arbetsorder.operationer.length);
            if (value) {
                this.reloadMaskinlagInfo();
            }
        }
    }

    private readonly setUpdateArbetsorderCommand = (start: string, slut: string): void => {
        this.arbetsorder.commandManager.addOrUpdate(new ResizeArbetsorderOperationCommand(c => {
            c.arbetsorderId = this.arbetsorder.id;
            c.maskinlagId = this.arbetsorder.maskinlagId;
            c.startdatum = start ? new Date(start) : null;
            c.slutdatum = slut ? new Date(slut) : null;
        }), c => c.arbetsorderId === this.arbetsorder.id);
    };

    private async setup(): Promise<void> {
        this.maskinlagLoaded = false;
        this.showMaskinoperationInfo = false;
        await this.loadMaskinlag();

        if (this.arbetsorder && this.arbetsorder.maskinlagId) {
            if (!this.calendarService.hasLoadedRange(this.skordningStartdatum, this.skotningSlutdatum)) {
                await this.calendarService.initHelgdagar(
                    moment(this.skordningStartdatum).subtract(6, "month").toISOString(),
                    moment(this.skotningSlutdatum).add(6, "month").toISOString());
            }

            this.maskinlagInfo = await this.arbetsorderMaskinlagService.getMaskinlagtabInfo(this.arbetsorder.id, this.arbetsorder.maskinlagId);
            await this.setupEntreprenor();
            this.showMaskinoperationInfo = true;
        }

        this.maskinlagLoaded = true;
    }
}
